import { ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { CheckboxModule } from 'primeng/checkbox';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { bootstrapApplication } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import { Router } from '@angular/router';
import { ODataModule } from 'angular-odata';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore } from '@ngrx/router-store';
import { ConfirmationService, MessageService } from 'primeng/api';
import { provideEffects } from '@ngrx/effects';
import * as Sentry from '@sentry/angular';
import { AllEnterpriseModule, LicenseManager, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-enterprise';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import { providePrimeNG } from 'primeng/config';
import { Buffer } from 'buffer';
import { PrimeNgThemePreset } from '@workspace-src/primeng-theme-preset';
import { PRIMEUI_LICENSE_KEY } from '@workspace-src/environments/primeui-license';
import { ErrorInterceptor } from './app/shared';
import * as globalState from './app/state/global';
import { environment } from './environments/environment';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
  removeStrayStateQueryParameter,
} from './app/auth';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { ApiBaseUrlInterceptor } from './app/shared/interceptors/ApiBaseUrlInterceptor';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).Buffer = Buffer;

// Only initialize Sentry in production and staging environments (not in local development).
// Skip it entirely on the unauthenticated /public pages: anonymous visitors must not be
// traced, replay-recorded, or shown the feedback widget. Public pages are standalone entry
// points (entering the authed app goes through an MSAL redirect, i.e. a full page reload),
// so an init-time check is sufficient.
if (environment.environmentName !== 'dev' && !window.location.pathname.startsWith('/public')) {
  // Defer Sentry initialization to improve initial load performance
  setTimeout(() => {
    Sentry.init({
      dsn: 'https://cb7888b63c650dfca67338849e0cdc12@o4507100441608192.ingest.de.sentry.io/4507102686150737',
      environment: environment.environmentName,
      tunnel: `${environment.apiConfig.apiBaseUrl}/api/sentry/tunnel`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
        Sentry.feedbackIntegration({
          showName: true,
          showEmail: true,
          isNameRequired: false,
          isEmailRequired: false,
          colorScheme: 'light',
          triggerLabel: 'Feedback',
          formTitle: 'Report your Feedback',
          submitButtonLabel: 'Submit',
          messagePlaceholder: 'Describe the problem or provide us your feedback',
          showBranding: false,
        }),
      ],
      denyUrls: ['localhost:4200'],
      tracePropagationTargets: ['localhost', /^https:\/\/.*\.qualicoat\.novaloop\.cloud/],
      sendClientReports: true,
      // Performance Monitoring
      tracesSampleRate: 0.5,
      // Session Replay — kept enabled but at minimal sampling. replayIntegration's rrweb recorder
      // mirrors the DOM and retains node references; on the SurveyJS report pages (heavy
      // enableIf/visibleIf re-render churn) those detached nodes were never released, so DOM nodes
      // climbed toward ~250k over a session on stage/prod and navigation got progressively slower
      // until a full page reload. replaysOnErrorSampleRate: 1.0 made nearly every session buffer the
      // DOM continuously, so it affected almost all users. Dropped to minimal so only a tiny sample
      // records. NOTE: any sampled session can still accumulate on report pages — add
      // replayIntegration({ mutationLimit, mutationBreadcrumbLimit }) so even sampled sessions bail
      // out of recording on DOM-heavy pages.
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 0.05,
    });
  }, 1000);
}

// pdf.js worker must be self-hosted: without this, ng2-pdf-viewer falls back to
// fetching the worker (which parses user-uploaded PDFs) from cdn.jsdelivr.net at runtime.
// The file is copied from ng2-pdf-viewer's own nested pdfjs-dist by an angular.json assets glob.
(window as { pdfWorkerSrc?: string }).pdfWorkerSrc = '/assets/pdfjs/pdf.worker.min.mjs';

// AG grid
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-095883}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Novaloop_AG}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{QUALICOAT_Workspace}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{QUALICOAT_Workspace}_need_to_be_licensed___{QUALICOAT_Workspace}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{1_February_2027}____[v3]_[01]_MTgwMTQ0MDAwMDAwMA==0576abd05158322020416bbabf10c690'
);
provideGlobalGridOptions({ theme: 'legacy' });

// Register all enterprise features
ModuleRegistry.registerModules([AllEnterpriseModule]);

// B2C's post-logout `?state=` echo would be mistaken for a sign-in response; drop it before MSAL reads the URL.
removeStrayStateQueryParameter();

bootstrapApplication(AppComponent, {
  providers: [
    // Animations
    provideZoneChangeDetection(),
    provideAnimationsAsync(),
    // NGRX
    provideStore({}),

    // global state
    provideState(globalState.name, globalState.reducer),
    provideEffects(globalState.GlobalStateEffects),

    provideRouterStore(),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production, connectInZone: true }),
    // PrimeNG
    providePrimeNG({
      license: PRIMEUI_LICENSE_KEY || undefined,
      theme: {
        preset: PrimeNgThemePreset,
        options: {
          // eslint-disable-next-line no-constant-binary-expression
          darkModeSelector: false || 'none',
        },
      },
    }),

    MessageService,
    ConfirmationService,
    importProvidersFrom(
      AppRoutingModule,
      AvatarModule,
      BadgeModule,
      CheckboxModule,
      ODataModule.forRoot({
        config: {
          serviceRootUrl: `${environment.apiConfig.apiBaseUrl}/odata/`,
          name: 'ODataBackend',
          options: { stringAsEnum: true },
        },
      }),
      RippleModule,
      StyleClassModule
    ),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // Base URL Interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBaseUrlInterceptor,
      multi: true,
    },
    // Authentication
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    // Error Interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    // sentry
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        dialogOptions: {
          user: {
            email: 'federico.marmori@novaloop.ch',
            name: 'Federico Marmori',
          },
        },
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideServiceWorker('sw-sync.js', {
      //enabled: !isDevMode(),
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
}).catch((err) => {
  console.error(err);
});
